/**
 * @fileOverview Toggles for form classes
 *
 * Provides methods that can be toggled on form elements, fieldsets form groups,
 * and form controls.
 *
 * ### States:
 *
 * The basic states are designed to always be inverted. An element either has
 * focus or it doesn't have focus, it either has been touched or it hasn't, etc.
 *
 * An element will have any of the following `is--*` OR `not--*` classes applied
 * to the its `classList`.
 *
 * - Pristine   — Value has not been changed yet by user.
 * - Touched    — Has received focus from user.
 * - Empty      — Has no value.
 * - Focused    — Currently has focus.
 * - Valid      — Has no validation errors.
 *
 * Also note that these classes should exist on elements at the form group
 * level, and can also exist at the form level. For example: A form group could
 * have `not--valid` but its parent form could have neiter `is--valid` nor
 * `not--valid`. Meaning: You can style the error state of the form groups,
 * elements and the form independently for different ways of providing feedback
 * to the user.
 *
 * @author Felix Dulfer <fdulfer@intothesource.com>
 */

/**
 * Binds a classname to an element for toggling
 *
 * This is purely for providing a unified interfaces for toggling specific
 * classNames.
 */
const toggleClass = (className: string) => (element: Element) => (force: boolean): void => {
    element.classList.toggle(className, force);
};

export const CLASSNAME_IS_PRISTINE: string = "is--pristine";
export const CLASSNAME_NOT_PRISTINE: string = "not--pristine";

export const CLASSNAME_IS_TOUCHED: string = "is--touched";
export const CLASSNAME_NOT_TOUCHED: string = "not--touched";

export const CLASSNAME_IS_EMPTY: string = "is--empty";
export const CLASSNAME_NOT_EMPTY: string = "not--empty";

export const CLASSNAME_IS_FOCUSED: string = "is--focused";
export const CLASSNAME_NOT_FOCUSED: string = "not--focused";

export const CLASSNAME_IS_VALID: string = "is--valid";
export const CLASSNAME_NOT_VALID: string = "not--valid";

export const CLASSNAME_IS_AUTOFILL: string = "is--autofill";

const toggleIsPristine = toggleClass(CLASSNAME_IS_PRISTINE);
const toggleNotPristine = toggleClass(CLASSNAME_NOT_PRISTINE);

const toggleIsTouched = toggleClass(CLASSNAME_IS_TOUCHED);
const toggleNotTouched = toggleClass(CLASSNAME_NOT_TOUCHED);

const toggleIsEmpty = toggleClass(CLASSNAME_IS_EMPTY);
const toggleNotEmpty = toggleClass(CLASSNAME_NOT_EMPTY);

const toggleIsFocused = toggleClass(CLASSNAME_IS_FOCUSED);
const toggleNotFocused = toggleClass(CLASSNAME_NOT_FOCUSED);

const toggleIsValid = toggleClass(CLASSNAME_IS_VALID);
const toggleNotValid = toggleClass(CLASSNAME_NOT_VALID);

export type TToggleFormClassApply = (force: boolean) => void;

export const togglePristine = (element: Element) => (isPristine: boolean): void => {
    toggleIsPristine(element)(isPristine === true);
    toggleNotPristine(element)(isPristine === false);
};

export const toggleTouched = (element: Element) => (isTouched: boolean): void => {
    toggleIsTouched(element)(isTouched === true);
    toggleNotTouched(element)(isTouched === false);
};

export const toggleEmpty = (element: Element) => (isEmpty: boolean): void => {
    toggleIsEmpty(element)(isEmpty === true);
    toggleNotEmpty(element)(isEmpty === false);
};

export const toggleFocused = (element: Element) => (isFocused: boolean): void => {
    toggleIsFocused(element)(isFocused === true);
    toggleNotFocused(element)(isFocused === false);
};

export const toggleValid = (element: Element) => (isValid: boolean): void => {
    toggleIsValid(element)(isValid === true);
    toggleNotValid(element)(isValid === false);
};



// WEBPACK FOOTER //
// ../common/material/form/toggle-form-class.ts